/** Shrink camera photos before upload (faster on mobile / fewer Safari timeouts). */

export async function compressImageForUpload(
  blob: Blob,
  opts: { maxSide?: number; quality?: number } = {},
): Promise<Blob> {
  if (!blob.type.startsWith("image/") || blob.type === "image/gif") {
    return blob;
  }
  // Skip tiny files
  if (blob.size < 400_000) return blob;

  const maxSide = opts.maxSide ?? 1600;
  const quality = opts.quality ?? 0.82;

  const bitmap = await createImageBitmap(blob);
  try {
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return blob;
    ctx.drawImage(bitmap, 0, 0, w, h);
    const out = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", quality),
    );
    return out && out.size < blob.size ? out : blob;
  } finally {
    bitmap.close();
  }
}

export function friendlyUploadError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err || "Upload failed");
  const lower = raw.toLowerCase();
  // Safari / WebKit network abort — often from interrupted fetch
  if (
    lower === "load failed" ||
    lower === "failed to fetch" ||
    lower.includes("networkerror") ||
    lower.includes("network request failed")
  ) {
    return "Upload was interrupted. Stay on this screen and tap Use photo again.";
  }
  if (lower.includes("session expired")) {
    return "Session expired — log in again, then retry.";
  }
  return raw;
}
